import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Star } from "lucide-react";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import { generateLocationString, generateSpaceName, getEventName } from "../../utils/spaceNamingUtils";
import logger from "../../utils/logger";

export type ListingSubcategory = { id: string | number; name: string };

type ListingGridProps = {
  categoryId: number;
  eyebrow: string;
  title: string;
  subcategories: ListingSubcategory[];
  /** Shown when a live listing has no photo of its own */
  fallbackImages: string[];
  /** Used in listing names and the empty state, e.g. "Stay" or "Experience" */
  itemLabel: string;
  emptyText: string;
  emptyCta: { label: string; to: string };
  /** Adds a one-line description under the name (Play) */
  showDescription?: boolean;
  /** Badge colour per sub-category name; defaults to brand blue */
  badgeColor?: (subcategoryName: string) => string;
};

/** Live listings for a category with sub-category filter chips (Stay, Play). */
export default function ListingGrid({
  categoryId,
  eyebrow,
  title,
  subcategories,
  fallbackImages,
  itemLabel,
  emptyText,
  emptyCta,
  showDescription = false,
  badgeColor,
}: ListingGridProps) {
  const history = useHistory();
  const [activeSubcategory, setActiveSubcategory] = useState("");
  const [spaces, setSpaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  // The section is hidden until the category has something to show
  const [hasListings, setHasListings] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchPublicSpaces = async () => {
      setLoading(true);
      try {
        const res = await spaceManagementService.searchPublicSpacesUnauthenticated({
          categoryId,
          ...(activeSubcategory ? { subCategoryId: activeSubcategory } : {}),
          pageSize: 6,
          status: "PUBLISHED",
        } as any);
        const responseData: any = res?.data;
        const items = Array.isArray(responseData?.content)
          ? responseData.content
          : Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData)
              ? responseData
              : [];
        if (isMounted) {
          setSpaces(items);
          if (items.length > 0) setHasListings(true);
        }
      } catch (err) {
        logger.warn(`Failed to load public spaces for category ${categoryId}:`, err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPublicSpaces();
    return () => {
      isMounted = false;
    };
  }, [categoryId, activeSubcategory]);

  // Nothing published in this category yet: leave the section out entirely
  if (!loading && !hasListings && spaces.length === 0) return null;

  const chip = (id: string, label: string) => (
    <button
      key={id || "all"}
      type="button"
      onClick={() => setActiveSubcategory(id)}
      className={`rounded-full px-[16px] py-2 text-[14px] font-semibold leading-5 transition-colors ${
        activeSubcategory === id
          ? "bg-[#017bef] text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)]"
          : "border-[1px] border-solid border-[#e5e7eb] bg-white text-[#717182] hover:text-[#0a0a0a]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section className="bg-[#f8fbff] py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
            <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
              {title}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {chip("", "All")}
            {subcategories.map(sub => chip(String(sub.id), sub.name))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-[20px] pt-12 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            [1, 2, 3].map(n => (
              <div
                key={n}
                className="h-[424px] animate-pulse rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white"
              />
            ))
          ) : spaces.length > 0 ? (
            spaces.slice(0, 6).map((space: any, idx: number) => {
              // Same naming as the homepage Explore section
              const firstInstance = space.instances?.[0];
              const name =
                getEventName(space, firstInstance) ||
                (space.hostDisplayName
                  ? `${space.subCategory?.name || space.subCategoryName || space.description || itemLabel} - ${space.hostDisplayName}`
                  : generateSpaceName(space, firstInstance, true));
              const image =
                space.media?.images?.[0] ||
                space.images?.[0]?.url ||
                space.coverImage ||
                space.imageUrl ||
                fallbackImages[idx % fallbackImages.length];
              const badge = space.subCategory?.name || space.subcategoryName || space.subCategoryName || itemLabel;
              const description = space.shortDescription || space.description;
              return (
                <div
                  key={space.id || idx}
                  className="overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white"
                >
                  <div className="relative h-[333px]">
                    <img src={image} alt={name} className="absolute inset-0 size-full object-cover" />
                    <span
                      className="absolute left-[12px] top-[12px] rounded-full px-2.5 py-[3px] text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white"
                      style={{ backgroundColor: badgeColor ? badgeColor(badge) : "#017bef" }}
                    >
                      {badge}
                    </span>
                    <span className="absolute right-[12px] top-[12px] flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold leading-[15px] text-[#0a0a0a]">
                      <ShieldCheck className="size-2.5 text-[#017bef]" />
                      Verified
                    </span>
                  </div>
                  <div className="p-[16px]">
                    <p className="flex items-center gap-1 text-[12px] leading-4 text-[#717182]">
                      <MapPin className="size-2.5 shrink-0" />
                      <span className="truncate">{generateLocationString(space, firstInstance)}</span>
                    </p>
                    <h3 className="truncate pt-0.5 text-[14px] font-bold leading-5 text-[#0a0a0a]">{name}</h3>
                    {showDescription && description && (
                      <p className="truncate pt-1 text-[12px] leading-4 text-[#717182]">{description}</p>
                    )}
                    <div className={`flex items-center justify-between ${showDescription ? "pt-[12px]" : "pt-1"}`}>
                      <span className="flex items-center gap-1 text-[12px] leading-4 text-[#717182]">
                        <Star className="size-2.5 fill-[#ffb900] text-[#ffb900]" />
                        Verified reviews
                      </span>
                      <button
                        type="button"
                        onClick={() => history.push(`/space/${space.id || ""}`)}
                        className="flex items-center gap-1 text-[12px] font-semibold leading-4 text-[#017bef] hover:underline"
                      >
                        Book
                        <ArrowRight className="size-2.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // Only reachable once a filter chip is active, so the chips stay available
            <div className="col-span-full rounded-2xl border-[1px] border-dashed border-[#e5e7eb] bg-white py-12 text-center">
              <p className="text-[14px] text-[#717182]">{emptyText}</p>
              <button
                type="button"
                onClick={() => history.push(emptyCta.to)}
                className="mt-[12px] text-[14px] font-semibold text-[#017bef] hover:underline"
              >
                {emptyCta.label}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
